<template>
  <div>
    <label
      :class="[
        'input-wrapper text-p-2',
        { error: hasError, filled: isValueHigherThanZero }
      ]"
    >
      <span class="text-p-1 text-prefix-input">R$</span>
      <input
        ref="input"
        v-model="localValue"
        v-money="formatterOptions"
        data-testid="single-input"
        type="tel"
        class="text-p-1"
        @focus="emitFocusEvent"
        @keypress.enter="emitEnterKeyEvent"
      />
      <span class="single-input__clean-area">
        <RoundedIcon
          v-if="hasError"
          aria-hidden="true"
          icon-name="ic_attention"
          :main-color="NebraskaColors.elementOnError"
          background-size="18"
          icon-size="18px"
        />
        <button
          v-else-if="isValueHigherThanZero && !isDisabled"
          class="icon right-icon-selectable"
          aria-label="Limpar valor"
          @click="resetLocalAmount"
        >
          <RoundedIcon
            aria-hidden="true"
            icon-name="ic_cancel"
            :main-color="NebraskaColors.elementPrimary"
            background-size="18"
            icon-size="12px"
          />
        </button>
      </span>
    </label>
    <div v-if="suggestions" class="suggestions-list">
      <button
        v-for="suggestion in suggestionsFormatted"
        :key="suggestion.label"
        :aria-label="`Adicionar ${suggestion.value}`"
        @click="handleSuggestionClick(suggestion)"
      >
        {{ suggestion.label }}
      </button>
    </div>
    <p
      :class="[
        'text-p-5 error-message',
        { 'error-message--visible': hasError }
      ]"
      :aria-hidden="hasError"
      role="alert"
    >
      {{ errorMessage }}
    </p>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Prop, Ref, Watch, Emit } from 'vue-property-decorator';
import { VMoney } from 'v-money';

import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import { toFixedWithoutRounding } from '@/modules/common/helpers/number';
import {
  formatCurrency,
  currencyToNumber
} from '@/modules/common/helpers/currency';

import { RoundedIcon } from '@warrenbrasil/nebraska-web';

interface SuggestionFormatted {
  value: number;
  label: string;
  isTotal: boolean;
}

@Component({
  inheritAttrs: false,
  name: 'SingleInput',
  components: { RoundedIcon },
  directives: { money: VMoney }
})
export default class SingleInput extends Vue {
  @Prop({ type: Number, default: 0 })
  private value!: number;

  @Prop({ type: Boolean, required: false })
  private suggestionAtFirst?: boolean;

  @Prop({ type: String, required: false })
  private suggestionLabel?: string;

  @Prop({ type: Array, default: () => [] })
  private suggestions!: number[];

  @Prop({ type: String, default: 'Ops, aconteceu um problema' })
  private errorMessage!: string;

  @Prop({ type: Boolean, default: false })
  private hasError!: boolean;

  @Prop({ type: Boolean, default: true })
  private autofocus!: boolean;

  @Prop({ type: Boolean, default: false })
  private isDisabled!: boolean;

  @Ref()
  private readonly input!: HTMLInputElement;

  @Watch('localValue', { immediate: true })
  handleLocalValueChange() {
    this.preventNegativeLocalValue();
    this.emitInputEvent();
  }

  private localValue = toFixedWithoutRounding(this.value, 2, true);

  private get isValueHigherThanZero() {
    return this.value > 0;
  }

  private get localValueAsNumber() {
    return currencyToNumber(this.localValue);
  }

  get suggestionsFormatted(): SuggestionFormatted[] {
    return this.suggestions.map((suggestion, index) => {
      const suggestedItem = this.suggestionAtFirst
        ? index === 0
        : this.suggestions.length - 1 === index;

      const label = suggestedItem
        ? this.suggestionLabel || 'Total'
        : this.formatSuggestion(suggestion);

      return { label, value: suggestion, isTotal: suggestedItem };
    });
  }

  mounted() {
    this.autofocus && this.input.focus();
  }

  @Emit('input')
  private emitInputEvent() {
    return this.localValueAsNumber;
  }

  @Emit('enter-key-event')
  public emitEnterKeyEvent() {}

  @Emit('focus')
  private emitFocusEvent() {}

  @Emit('suggestion-click')
  private emitSuggestionEvent(suggestionLabel: string) {
    return suggestionLabel;
  }

  private preventNegativeLocalValue() {
    this.localValue = this.localValue.replace('-', '');
  }

  private handleSuggestionClick(suggestion: SuggestionFormatted) {
    const { value, label, isTotal } = suggestion;

    isTotal ? this.setLocalAmount(value) : this.increaseLocalAmount(value);
    this.emitSuggestionEvent(label);
  }

  private setLocalAmount(newAmount: number) {
    this.localValue = toFixedWithoutRounding(newAmount, 2, true);
  }

  private increaseLocalAmount(newAmount: number) {
    const total = this.value + newAmount;
    this.setLocalAmount(total);
  }

  public resetLocalAmount() {
    this.localValue = '0';
  }

  private get NebraskaColors() {
    return NebraskaColors;
  }

  private formatSuggestion(originalNumber: number) {
    const formattedNumber = formatCurrency(originalNumber);
    const [number] = formattedNumber.split(',');

    return `+ ${number}`;
  }

  private get formatterOptions() {
    return {
      decimal: ',',
      thousands: '.',
      prefix: '',
      precision: 2
    };
  }
}
</script>

<style lang="less" scoped>
.single-input {
  &__clean-area {
    width: 18px;
  }
}

.text-prefix-input {
  font-weight: normal;
  font-size: 72px;
  padding-right: @size-spacing-x400;
}

.input-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: none;
  box-shadow: 0px 1px 0px 0px @divider-primary;
  background-color: transparent;
  padding: 0 @size-spacing-x400;
  transition: all 0.2s cubic-bezier(0.55, 0, 0.1, 1);

  &:hover {
    box-shadow: 0px 1px 0px 0px @neutral-primary;
  }

  &.filled {
    box-shadow: 0px 1px 0px 0px @neutral-primary;
    input {
      color: @text-primary;
    }
  }

  &:focus-within {
    box-shadow: 0px 2px 0px 0px @neutral-primary;
  }
  &:active {
    transform: unset;
  }

  &.error {
    box-shadow: 0px 1px 0px 0px @element-on-error;
    &:focus-within {
      box-shadow: 0px 2px 0px 0px @element-on-error;
    }
  }

  input {
    width: 100%;
    flex-grow: 1;
    background: transparent;
    border: none;
    outline: none;
    padding: 0 @size-spacing-x150;
    color: @text-placeholder;
    font-weight: normal;
    font-size: 72px;
    &:disabled {
      color: @element-disabled !important;
    }

    // Removes mobile unwanted highlight border on Chrome/Firefox
    &:-webkit-autofill {
      transition: background-color 0s ease-in-out 1000000s;
    }
  }
}

.right-icon-selectable {
  cursor: pointer;
  transition: 0.2s ease-in all;
}

.icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: row;
  padding: 0;
  background-color: transparent;
  border: unset;
}

.suggestions-list {
  display: flex;
  padding: @size-spacing-x400 0 @size-spacing-x400;
  width: 100%;
  overflow: auto;

  button {
    padding: @size-spacing-x300 @size-spacing-x400;
    background-color: @background-tertiary;
    border-radius: 100px;
    font-weight: bold;
    margin-right: @size-spacing-x350;
    white-space: nowrap;
    text-transform: uppercase;

    &:last-child {
      margin-right: 0px;
    }
  }
}
.error-message {
  color: @element-on-error;
  min-height: @size-spacing-x500;
  opacity: 0;
  transition: opacity 200ms ease;

  &--visible {
    opacity: 1;
  }
}

@media @mobile {
  .text-prefix-input {
    font-size: 20px;
    line-height: 28px;
    font-weight: 700;
  }
  .input-wrapper {
    input {
      font-size: 40px;
      font-weight: 700;
      line-height: 48px;
    }
  }
}
</style>
